import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { SxProps } from '@mui/system';
import { GpsStatus } from '@treadinc/horizon-api-spec';
import React, { useEffect, useRef, useState } from 'react';

import GpsStatusIcon from '~components/Common/GpsStatusIcon';
import { LocationData } from '~components/LocationData/LocationData';
import { DeviceStatusRead } from '~hooks/useDeviceStatus/models';

interface GpsStatusPopoverProps {
  gpsStatus: GpsStatus;
  onGetDeviceStatus: () => Promise<DeviceStatusRead | null>;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right' | 'center';
  };
  sx?: SxProps;
  children?: React.ReactNode;
  showIdleBadge?: boolean;
}

export default function GpsStatusPopover({
  gpsStatus,
  onGetDeviceStatus,
  anchorOrigin = { vertical: 'top', horizontal: 'left' },
  sx,
  children,
  showIdleBadge,
}: GpsStatusPopoverProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatusRead | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, []);

  const getDeviceStatus = async () => {
    setIsLoading(true);
    try {
      const status = await onGetDeviceStatus();
      if (status) {
        setDeviceStatus(status);
        setIsError(false);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopoverOpen = async () => {
    await getDeviceStatus();
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setIsPopoverOpen(true);
  };

  const handleMouseEnter = () => {
    if (isPopoverOpen) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      handlePopoverOpen();
    }, 350);
  };

  const handlePaperMouseEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  };

  const handleMouseLeave = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsPopoverOpen(false);
    }, 200);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'inline-block', ...sx }}
      >
        {children || (
          <GpsStatusIcon gpsStatus={gpsStatus} showIdleBadge={showIdleBadge} />
        )}
      </Box>
      <Popover
        id="gps-status-popover"
        open={isPopoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => {
          if (showTimerRef.current) clearTimeout(showTimerRef.current);
          setIsPopoverOpen(false);
        }}
        anchorOrigin={anchorOrigin}
        slotProps={{
          paper: {
            onMouseEnter: handlePaperMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseDown: (e) => e.stopPropagation(),
            onMouseUp: (e) => e.stopPropagation(),
            onClick: (e) => e.stopPropagation(),
            onSelect: (e) => e.stopPropagation(),
            sx: {
              pointerEvents: 'auto',
              p: 1,
              userSelect: 'text',
              '& *': {
                userSelect: 'text',
              },
            },
          },
        }}
      >
        <LocationData
          deviceStatus={deviceStatus}
          isLoading={isLoading}
          isError={isError}
          refetchDeviceStatus={getDeviceStatus}
        />
      </Popover>
    </>
  );
}
